import { ng } from "@common/ngds";
import { Drawer, lerp, PathBuilder } from "@common/components/Rendering";
import {
  IconSize,
  SlashStyle,
  support_style,
  viewport_icon_size,
  viewport_slash_size,
  ground_style,
  slash_style,
  ArrowId,
} from "../ViewportStyles";
import { GraphDirection } from "./SideView";
import { SupportConnectivityType } from "@common/helpers/sharedTypes";
import { getSupports } from "../../helpers/supports";
import { DiagramResults } from "./drawSectionForceDiagrams";

const draw_bottom_ground = (
  g: Drawer<ArrowId>,
  left: number,
  right: number,
  vert: number,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  g.drawLine([left, vert], [right, vert], ground_style, {
    display: is_visible,
  });

  const start = left + slash.size;
  const end = right;
  const rough_step = slash.size;

  const dist = end - start;
  const count = Math.max(slash.min_count - 1, Math.abs(Math.round(dist / rough_step)));

  for (let iter = 0; iter <= count; iter += 1) {
    const g_index = lerp(start, end, iter / count);
    g.drawLine([g_index, vert], [g_index - slash.size, vert + slash.size], slash_style, {
      display: is_visible,
    });
  }
};
const draw_top_ground = (
  g: Drawer<ArrowId>,
  left: number,
  right: number,
  vert: number,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  g.drawLine([left, vert], [right, vert], ground_style, {
    display: is_visible,
  });

  // const start = left + slash.size;
  // const end = right;
  // const rough_step = slash.size;

  // const dist = end - start;
  // const count = Math.max(slash.min_count - 1, Math.abs(Math.round(dist / rough_step)));

  // for (let iter = 0; iter <= count; iter += 1) {
  //   const g_index = lerp(start, end, iter / count);
  //   g.drawLine([g_index, vert], [g_index - slash.size, vert - slash.size], slash_style, {
  //     display: is_visible,
  //   });
  // }
};

const draw_right_wall = (
  g: Drawer<ArrowId>,
  hor: number,
  top: number,
  bottom: number,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  g.drawLine([hor, top], [hor, bottom], ground_style, {
    display: is_visible,
  });

  const start = bottom - slash.size;
  const end = top;
  const rough_step = slash.size;

  const dist = end - start;
  const count = Math.max(slash.min_count - 1, Math.abs(Math.round(dist / rough_step)));

  for (let iter = 0; iter <= count; iter += 1) {
    const g_index = lerp(start, end, iter / count);
    g.drawLine([hor, g_index], [hor + slash.size, g_index + slash.size], slash_style, {
      display: is_visible,
    });
  }
};

const draw_left_wall = (
  g: Drawer<ArrowId>,
  hor: number,
  top: number,
  bottom: number,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  g.drawLine([hor, top], [hor, bottom], ground_style, {
    display: is_visible,
  });

  const start = bottom - slash.size;
  const end = top;
  const rough_step = slash.size;

  const dist = end - start;
  const count = Math.max(slash.min_count - 1, Math.abs(Math.round(dist / rough_step)));

  for (let iter = 0; iter <= count; iter += 1) {
    const g_index = lerp(start, end, iter / count);
    g.drawLine([hor, g_index], [hor - slash.size, g_index + slash.size], slash_style, {
      display: is_visible,
    });
  }
};

const draw_support_right_roller = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  size: IconSize,
  slash_size: SlashStyle,
  is_visible: boolean,
) => {
  const rad = size.size / 2;
  const extra = size.extra / 2; // half because the circle makes the dist looks bigger

  g.drawCircle([hor + rad, ver], rad, support_style, {
    display: is_visible,
  });
  draw_right_wall(
    g,
    hor + size.size,
    ver - size.size / 2 - size.extra / 2,
    ver + size.size / 2 + size.extra / 2,
    slash_size,
    is_visible,
  );
};

const draw_support_right_pinned = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  size: IconSize,
  slash_size: SlashStyle,
  is_visible: boolean,
) => {
  g.drawPath(
    support_style,
    new PathBuilder()
      .moveTo([hor, ver]) // left point
      .drawLineTo([hor + size.size, ver - size.size / 2]) // up right
      .drawLineTo([hor + size.size, ver + size.size / 2]) // down right
      .closePath(), // close to top
    {
      display: is_visible,
    },
  );
  draw_right_wall(
    g,
    hor + size.size,
    ver - size.size / 2 - size.extra / 2,
    ver + size.size / 2 + size.extra / 2,
    slash_size,
    is_visible,
  );
};

const draw_support_right_rigid = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  size: IconSize,
  slash_size: SlashStyle,
  is_visible: boolean,
) => {
  draw_right_wall(
    g,
    hor,
    ver - size.size / 2 - size.extra / 2,
    ver + size.size / 2 + size.extra / 2,
    slash_size,
    is_visible,
  );
};

const draw_support_bottom_pinned = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  size: IconSize,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  g.drawPath(
    support_style,
    new PathBuilder()
      .moveTo([hor, ver]) // top point
      .drawLineTo([hor - size.size / 2, ver + size.size]) // down left
      .drawLineTo([hor + size.size / 2, ver + size.size]) // full right
      .closePath(), // close to top
    {
      display: is_visible,
    },
  );

  draw_bottom_ground(
    g,
    hor - size.size / 2 - size.extra / 2,
    hor + size.size / 2 + size.extra / 2,
    ver + size.size,
    slash,
    is_visible,
  );
};

const draw_support_rigid_roller_h = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  column_size: number,
  size: IconSize,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  const rad = 0.05; // same small radius as top_rigid circles

  // Thick horizontal ground line — same width as top_rigid ground
  g.drawLine(
    [hor - column_size / 2 - size.extra / 2, ver],
    [hor + column_size / 2 + size.extra / 2, ver],
    ground_style,
    { display: is_visible },
  );

  // Two small circles side by side indicating horizontal rolling freedom
  g.drawCircle([hor - rad * 1.5, ver + rad], rad, support_style, {
    display: is_visible,
  });
  g.drawCircle([hor + rad * 1.5, ver + rad], rad, support_style, {
    display: is_visible,
  });

  // Floor ground line with hatching below the circles
  draw_bottom_ground(
    g,
    hor - size.size / 2 - size.extra / 2,
    hor + size.size / 2 + size.extra / 2,
    ver + rad * 2,
    slash,
    is_visible,
  );
};

const draw_support_rigid = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  column_size: number,
  size: IconSize,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  draw_bottom_ground(
    g,
    hor - column_size / 2 - size.extra / 2,
    hor + column_size / 2 + size.extra / 2,
    ver,
    slash,
    is_visible,
  );
};

const draw_support_rigid_roller_v = (
  g: Drawer<ArrowId>,
  hor: number,
  ver: number,
  column_size: number,
  size: IconSize,
  slash: SlashStyle,
  is_visible: boolean,
) => {
  draw_top_ground(
    g,
    hor - column_size / 2 - size.extra / 2,
    hor + column_size / 2 + size.extra / 2,
    ver,
    slash,
    is_visible,
  );

  // right side vertical line
  hor += column_size / 2 + size.extra / 2;
  const top = ver - size.size / 2 - size.extra / 2;
  const bottom = ver + size.size / 2 + size.extra / 2;
  g.drawLine([hor, top], [hor, bottom], ground_style, {
    display: is_visible,
  });

  const rad = 0.05; // size.size / 4;
  hor += 2 * rad;
  draw_right_wall(
    g,
    hor,
    ver - size.size / 2 - size.extra / 2,
    ver + size.size / 2 + size.extra / 2,
    slash,
    is_visible,
  );

  hor -= rad;
  g.drawCircle([hor, ver - rad * 1.5], rad, support_style, {
    display: is_visible,
  });
  g.drawCircle([hor, ver + rad * 1.5], rad, support_style, {
    display: is_visible,
  });

  // left side vertical line
  hor -= rad + column_size + size.extra;
  g.drawLine([hor, top], [hor, bottom], ground_style, {
    display: is_visible,
  });

  hor -= 2 * rad;
  draw_left_wall(g, hor, ver - size.size / 2 - size.extra / 2, ver + size.size / 2 + size.extra / 2, slash, is_visible);

  hor += rad;
  g.drawCircle([hor, ver - rad * 1.5], rad, support_style, {
    display: is_visible,
  });
  g.drawCircle([hor, ver + rad * 1.5], rad, support_style, {
    display: is_visible,
  });
};

const draw_support_icon = (args: {
  g: Drawer<ArrowId>;
  sup_type: SupportConnectivityType;
  isBottom: boolean;
  isTop: boolean;
  is_visible: boolean;
  xCoord: number;
  column_size: number;
  bottom_support_hor: number;
  right_support_hor: number;
}) => {
  switch (args.sup_type) {
    case "free":
      // todo(Gustav): is this not rendered?
      break;
    case "rigid":
      // if (args.isBottom) {
      draw_support_rigid(
        args.g,
        args.bottom_support_hor,
        args.xCoord,
        args.column_size,
        viewport_icon_size,
        viewport_slash_size,
        args.is_visible,
      );
      // } else {
      //   draw_support_right_rigid(
      //     args.g,
      //     args.right_support_hor,
      //     args.xCoord,
      //     viewport_icon_size,
      //     viewport_slash_size,
      //     args.is_visible,
      //   );
      // }
      break;
    case "pinned":
      if (args.isBottom) {
        draw_support_bottom_pinned(
          args.g,
          args.bottom_support_hor,
          args.xCoord,
          viewport_icon_size,
          viewport_slash_size,
          args.is_visible,
        );
      } else {
        draw_support_right_pinned(
          args.g,
          args.right_support_hor,
          args.xCoord,
          viewport_icon_size,
          viewport_slash_size,
          args.is_visible,
        );
      }
      break;
    case "rigid_roller":
      if (args.isBottom) {
        draw_support_rigid_roller_h(
          args.g,
          args.bottom_support_hor,
          args.xCoord,
          args.column_size,
          viewport_icon_size,
          viewport_slash_size,
          args.is_visible,
        );
      } else {
        draw_support_rigid_roller_v(
          args.g,
          args.bottom_support_hor,
          args.xCoord,
          args.column_size,
          viewport_icon_size,
          viewport_slash_size,
          args.is_visible,
        );
      }
      break;
    case "roller":
      if (args.isBottom) {
        // not selectable in ui
      } else {
        draw_support_right_roller(
          args.g,
          args.right_support_hor,
          args.xCoord,
          viewport_icon_size,
          viewport_slash_size,
          args.is_visible,
        );
      }
      break;
  }
};

export const drawSupport = (
  g: Drawer<ArrowId>,
  props: {
    graphDirection: GraphDirection;
    center_line_hor: number;
    column_size: number;
    column_height: number;
    project: ng.frontend.column.Project;
    is_visible: boolean;
    results: DiagramResults;
    selectedLcombGuid: string;
  },
) => {
  const { center_line_hor, column_size } = props;
  const supports = getSupports(props.project, props.graphDirection === "xy" ? "Y" : "Z");

  const right_support_hor = center_line_hor + column_size / 2;
  const bottom_support_hor = center_line_hor;

  for (const sup of supports) {
    const xCoord = -sup.x;
    const isBottom = sup.isBaseSupport;
    const isTop = Math.abs(xCoord + props.column_height) < 1e-3; // consider as top if it's very close to the column height

    // draw support
    draw_support_icon({
      g,
      sup_type: sup.type,
      right_support_hor,
      bottom_support_hor,
      xCoord,
      isBottom,
      isTop,
      is_visible: props.is_visible,
      column_size,
    });
  }
};
